// ── Quotation — print document, MOZI template (mozPrint.js) ──
import {
  esc, has, fmtDate, fmtNum, thumbCell, heroHtml, tabHtml, detailsHtml, buildMozDocument, brandWordOf,
} from './mozPrint';
import { quoteTotals, splitGst } from '../../utils/quotationTotals';

const money = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', rejected: 'Rejected', revised: 'Revised' };

// Indian-format amount in words (rupees + paise)
function inWords(amount) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
    'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = n => (n < 20 ? ones[n] : `${tens[Math.floor(n / 10)]}${n % 10 ? ' ' + ones[n % 10] : ''}`);
  const three = n => `${n >= 100 ? ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' : '') : ''}${n % 100 ? two(n % 100) : ''}`;
  const words = n => {
    if (!n) return 'Zero';
    const parts = [];
    const crore = Math.floor(n / 1e7); n %= 1e7;
    const lakh = Math.floor(n / 1e5); n %= 1e5;
    const thousand = Math.floor(n / 1000); n %= 1000;
    if (crore) parts.push(`${words(crore)} Crore`);
    if (lakh) parts.push(`${two(lakh)} Lakh`);
    if (thousand) parts.push(`${two(thousand)} Thousand`);
    if (n) parts.push(three(n));
    return parts.join(' ');
  };
  const total = Math.round(Number(amount || 0) * 100);
  const rupees = Math.floor(total / 100), paise = total % 100;
  return `Rupees ${words(rupees)}${paise ? ` and ${two(paise)} Paise` : ''} Only`;
}

export function buildQuotationHtml(q, settings = {}, origin = window.location.origin) {
  const items = q.items || [];
  const qNo = `${q.quote_no}${q.revision ? ` Rev ${q.revision}` : ''}`;
  const details = [
    ['Quotation No.', qNo, ' big'],
    ['Date', fmtDate(q.quote_date)],
    ['Client', q.client_name],
    ['Valid Until', fmtDate(q.valid_until)],
    ['Manufacturing Order', q.po_no],
    ['Project', [q.project_name || q.mo_project_name, q.project_code].filter(has).join(' / ')],
    ['Client GSTIN', q.client_gstin],
    ['Prepared By', q.created_by_name],
  ];

  const rows = items.map((it, i) => `<tr>
      <td class="c sn">${i + 1}</td>
      <td class="pc">${esc(it.proto_code || '')}</td>
      <td class="pn">
        <div class="pname">${esc(it.item_name)}</div>
        ${has(it.area) ? `<div class="psub">Area: ${esc(it.area)}</div>` : ''}
        ${has(it.size) ? `<div class="psub">Size: ${esc(it.size)}</div>` : ''}
        ${has(it.finishes) ? `<div class="psub">Finish: ${esc(it.finishes)}</div>` : ''}
        ${has(it.description) ? `<div class="psub">${esc(it.description)}</div>` : ''}
      </td>
      <td class="im">${thumbCell(it.images, 'No image', true)}</td>
      <td class="c qty">${esc(fmtNum(it.qty))}<div class="unit">${esc(it.unit || '')}</div></td>
      <td class="n">${money(it.rate)}</td>
      <td class="n b">${money(it.amount)}</td>
    </tr>`).join('');

  // Same rules as the form and the server: GST only when "Calculate GST" is on (older quotations: on)
  const T = quoteTotals({ items, charges: q.charges || [], calc_gst: q.calc_gst ?? 1, discount_amount: q.discount_amount, gst_percent: q.gst_percent });
  const pct = r => fmtNum(q.gst_type === 'inter' ? r : r / 2);
  const taxLine = (x, what) => splitGst(x.gst, q.gst_type)
    .map(y => `<tr><td>${y.label} @ ${pct(x.rate)}%${what}</td><td class="n">${money(y.amount)}</td></tr>`).join('');
  const itemTax = T.taxLines.filter(x => x.src === 'items').map(x => taxLine(x, T.chargeRows.length ? ' on items' : '')).join('');
  const chargeTax = T.taxLines.filter(x => x.src === 'charges').map(x => taxLine(x, ' on charges')).join('');
  const chargeRows = T.chargeRows.map((c, i) => `<tr>
      <td class="c sn">${i + 1}</td>
      <td>${esc(c.description)}</td>
      <td class="n">${money(c.amount)}</td>
      <td class="c">${T.calc && c.with_gst ? `${fmtNum(c.gst_percent)}%` : 'Without GST'}</td>
      <td class="n">${c.gst_amount > 0 ? money(c.gst_amount) : '—'}</td>
      <td class="n b">${money(c.line_total)}</td>
    </tr>`).join('');

  const company = settings.company_name || '';
  // Bank details from Settings → Bank Details (printed unless switched off there)
  const bank = [
    ['Account Name', settings.bank_account_name], ['Bank', settings.bank_name], ['Account No.', settings.bank_account_no],
    ['IFSC', settings.bank_ifsc], ['Branch', settings.bank_branch], ['Account Type', settings.bank_account_type], ['UPI', settings.bank_upi],
  ].filter(([, v]) => has(v));
  const showBank = String(settings.bank_show_on_quotation || 'yes') !== 'no' && bank.some(([k]) => k === 'Account No.' || k === 'UPI');
  const bankHtml = showBank ? `<div class="bank"><div class="k">Bank Details for Payment</div>
      <table>${bank.map(([k, v]) => `<tr><td class="bk">${esc(k)}</td><td class="bv">${esc(v)}</td></tr>`).join('')}</table></div>` : '';
  const body = `
  ${heroHtml({ brandWord: brandWordOf(settings), titleTop: 'Price', titleAccent: 'Quotation', sub: STATUS[q.status] || '' })}

  ${tabHtml('doc', 'Quotation Details', '62mm')}
  ${detailsHtml(details)}
  ${has(q.client_address) || has(q.subject) ? `<div class="qbox">
    ${has(q.subject) ? `<div><span class="k">Subject</span> <b>${esc(q.subject)}</b></div>` : ''}
    ${has(q.client_address) ? `<div><span class="k">Client Address</span> ${esc(q.client_address)}</div>` : ''}
  </div>` : ''}

  ${tabHtml('box', 'Items &amp; Prices', '58mm')}
  <table class="items">
    <thead><tr>
      <th class="c sn">S.No.</th><th>Proto Code</th><th>Item &amp; Description</th><th class="c">Image</th>
      <th class="c">Qty.</th><th class="n">Rate (₹)</th><th class="n">Amount (₹)</th>
    </tr></thead>
    <tbody>${rows || '<tr><td colspan="7" class="none">No items</td></tr>'}</tbody>
  </table>

  ${T.chargeRows.length ? `<div class="sum-wrap">
  ${tabHtml('layers', 'Additional Charges', '58mm')}
  <table class="items charges">
    <thead><tr>
      <th class="c sn">S.No.</th><th>Description</th><th class="n">Amount (₹)</th><th class="c">GST</th><th class="n">GST (₹)</th><th class="n">Total (₹)</th>
    </tr></thead>
    <tbody>${chargeRows}</tbody>
  </table></div>` : ''}

  <div class="qtot">
    <div class="qleft">
      <div class="words"><span class="k">Amount in words</span><div>${esc(inWords(T.total))}</div>
        ${!T.calc ? '<div class="nogst">GST not included in this quotation.</div>' : ''}</div>
      ${bankHtml}
    </div>
    <table class="sumt">
      <tbody>
        <tr><td>${T.chargeRows.length ? 'Items Subtotal' : 'Subtotal'}</td><td class="n">${money(T.subtotal)}</td></tr>
        ${T.discount > 0 ? `<tr><td>Discount</td><td class="n">− ${money(T.discount)}</td></tr><tr><td>Taxable Value</td><td class="n">${money(T.taxable)}</td></tr>` : ''}
        ${itemTax}
        ${T.chargesAmount > 0 ? `<tr><td>Additional Charges</td><td class="n">${money(T.chargesAmount)}</td></tr>` : ''}
        ${chargeTax}
        ${T.gst > 0 ? `<tr class="tg"><td>Total GST</td><td class="n">${money(T.gst)}</td></tr>` : ''}
        <tr class="grand"><td>Grand Total</td><td class="n">₹ ${money(T.total)}</td></tr>
      </tbody>
    </table>
  </div>

  ${has(q.terms) || has(q.notes) ? `<div class="sum-wrap">
    ${tabHtml('check', 'Terms &amp; Conditions', '62mm')}
    <div class="terms">${has(q.terms) ? esc(q.terms) : ''}${has(q.notes) ? `${has(q.terms) ? '\n\n' : ''}${esc(q.notes)}` : ''}</div>
  </div>` : ''}

  <div class="qsign">
    <div class="sg"><div class="sk">Client Acceptance</div><div class="sv"></div><div class="sl">Name / Sign / Date</div></div>
    <div class="sg"><div class="sk">For ${esc(company)}</div><div class="sv"></div><div class="sl">Authorised Signatory</div></div>
  </div>`;

  const extraCss = `
    .items .n { text-align: right; white-space: nowrap; }
    .items .b { font-weight: 800; }
    .items .unit { font-size: 6.6pt; color: #8a8478; font-weight: 600; }
    .qbox { background: #fff; border: .3mm solid #ddd5ca; border-top: 0; padding: 2.4mm 3mm; font-size: 8.2pt; display: grid; gap: 1.4mm; }
    .qbox .k, .qtot .k { font-size: 7.2pt; color: #8a8478; margin-right: 2mm; }
    .qtot { display: grid; grid-template-columns: 1fr 78mm; gap: 4mm; margin-top: 3mm; break-inside: avoid; align-items: start; }
    .qtot .words { background: #fff; border: .3mm solid #ddd5ca; padding: 2.4mm 3mm; font-size: 8.4pt; font-weight: 600; }
    table.sumt { width: 100%; border-collapse: collapse; background: #fff; border: .3mm solid #ddd5ca; }
    .sumt td { border: .3mm solid #ddd5ca; padding: 1.8mm 3mm; font-size: 8.4pt; }
    .sumt .n { text-align: right; white-space: nowrap; }
    .sumt tr.tg td { font-weight: 700; background: #f6f1ea; }
    .qleft { display: grid; gap: 3mm; align-content: start; }
    .bank { background: #fff; border: .3mm solid #ddd5ca; padding: 2.4mm 3mm; }
    .bank .k { display: block; font-size: 7.2pt; color: #8a8478; margin-bottom: 1.2mm; }
    .bank table { border-collapse: collapse; width: 100%; }
    .bank td { padding: .7mm 0; font-size: 8.2pt; vertical-align: top; }
    .bank .bk { color: #8a8478; width: 26mm; white-space: nowrap; }
    .bank .bv { font-weight: 700; color: #2b2a27; word-break: break-word; }
    .qtot .nogst { margin-top: 1.6mm; font-size: 7.4pt; font-weight: 600; color: #8a5a1c; }
    .items.charges td { font-size: 8pt; }
    .sumt tr.grand td { background: #2b2a27; color: #fff; font: 800 10pt Montserrat, Inter, sans-serif; }
    .sum-wrap { break-inside: avoid; }
    .terms { background: #fff; border: .3mm solid #ddd5ca; border-top: 0; padding: 3mm; font-size: 7.8pt; white-space: pre-wrap; color: #3d3a35; }
    .qsign { display: grid; grid-template-columns: 1fr 1fr; margin-top: 5mm; background: #fff; border: .3mm solid #ddd5ca; break-inside: avoid; }
    .qsign .sg { padding: 2.4mm 3mm; min-height: 22mm; display: flex; flex-direction: column; border-right: .3mm solid #ddd5ca; }
    .qsign .sg:last-child { border-right: 0; }
    .qsign .sk { font-size: 7.2pt; color: #8a8478; }
    .qsign .sv { flex: 1; border-bottom: .3mm solid #b3aa9e; margin-top: 3mm; }
    .qsign .sl { font-size: 6.8pt; color: #8a8478; margin-top: 1mm; }
  `;
  return buildMozDocument({ docTitle: `${qNo} — Quotation`, body, settings, origin, extraCss });
}
